import React, { useRef, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "../../../redux/store";
import {
  Sparkles,
  ArrowUp,
  Check,
  ChevronLeft,
  ChevronRight,
  Zap,
} from "lucide-react";
import { submitStreaming, Message, ChatRequest } from "../qa/actions";
import MessageItem from "../../../components/messageItem";
import useDeviceType from "../../../shared/hooks/useDeviceType";
import { DeviceTypes } from "../../../shared/utils/enums";
import { ChatType } from "../../../shared/utils/enums";
import { TabWithCategories } from "../../../interfaces/api/chat/config";
import { showModal } from "../../../connected-components/modal/reducer";
import { ModalType } from "../../../shared/utils/enums";
import CasesModal from "../../../components/casesModal";
import ClinicalCaseSkeleton from "./components/ClinicalCaseSkeleton";
import Button from "../../../components/button";
import { ButtonVariant } from "../../../shared/utils/enums";

// Extend ChatRequest to include category_id
interface ClinicalCaseRequest extends ChatRequest {
  category_id?: number;
}

const ClinicalCaseScreen: React.FC = () => {
  const { tabId } = useParams<{ tabId: string }>();
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const allCategories = useSelector(
    (state: RootState) => state.auth.categories,
  );
  const isLoadingCategories = useSelector(
    (state: RootState) => state.auth.isLoadingCategories,
  );
  const dispatch = useDispatch();

  // Convert tabId from URL format (with dashes) back to tab_value format (with underscores)
  const tabValue = tabId?.replace(/-/g, "_");

  // Find the matching category by tab_value
  const matchingTab = allCategories.find(
    (tab: TabWithCategories) => tab.tab_value === tabValue,
  );
  const categories = matchingTab?.categories || [];

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [streamingContent, setStreamingContent] = useState("");
  const [currentStreamingMessageId, setCurrentStreamingMessageId] = useState<
    string | null
  >(null);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<{
    id: number;
    title: string;
  } | null>(null);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamContentRef = useRef("");
  const categoriesScrollRef = useRef<HTMLDivElement>(null);

  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.mobile;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollCategories = (direction: "left" | "right") => {
    if (categoriesScrollRef.current) {
      const containerWidth = categoriesScrollRef.current.clientWidth;
      const scrollAmount = containerWidth - 40; // Full page minus some padding
      const currentScroll = categoriesScrollRef.current.scrollLeft;
      const newScroll =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;

      categoriesScrollRef.current.scrollTo({
        left: newScroll,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (categories.length === 1 && !selectedCategory) {
      setSelectedCategory(categories[0]);
    }
  }, [categories, selectedCategory]);

  const handleSend = async (messageContent: string) => {
    if (!selectedCategory) {
      alert("Please select a category first");
      return;
    }

    setIsMessageSending(true);
    setInput("");

    const userMessage: Message = {
      id: Date.now().toString(),
      content: messageContent,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

    const aiMessageId = (Date.now() + 1).toString();
    const aiMessage: Message = {
      id: aiMessageId,
      content: "",
      isUser: false,
      timestamp: new Date(),
      isStreaming: true,
    };
    setMessages((prev) => [...prev, aiMessage]);

    setStreamingContent("");
    streamContentRef.current = "";
    setCurrentStreamingMessageId(aiMessageId);

    try {
      const request: ClinicalCaseRequest = {
        question: userMessage.content,
        type: ChatType.ClinicalCase,
        category_id: selectedCategory.id,
      };

      await submitStreaming(
        request as ChatRequest,
        (chunk: string) => {
          const cleanChunk = chunk.replace(/\[DONE\]/g, "");
          if (!cleanChunk) return;

          // Update the ref with accumulated content
          streamContentRef.current = streamContentRef.current + cleanChunk;

          // Update the streaming content state
          setStreamingContent(streamContentRef.current);

          // Update the message in real-time
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiMessageId && !msg.isUser
                ? {
                    ...msg,
                    content: streamContentRef.current,
                    isStreaming: true,
                  }
                : msg,
            ),
          );
        },
        () => {
          // Completion callback - use the accumulated content from ref
          const finalContent = streamContentRef.current;

          // Update the final message and stop streaming
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiMessageId && !msg.isUser
                ? { ...msg, content: finalContent, isStreaming: false }
                : msg,
            ),
          );

          // Clean up streaming state
          setStreamingContent("");
          setCurrentStreamingMessageId(null);
          setIsMessageSending(false);

          // Reset the ref for next message
          streamContentRef.current = "";
        },
        (error: string) => {
          console.error("Streaming error:", error);

          // Update the message with error and stop streaming
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiMessageId && !msg.isUser
                ? {
                    ...msg,
                    content: "Sorry, an error occurred. Please try again.",
                    isStreaming: false,
                  }
                : msg,
            ),
          );

          // Clean up streaming state
          setStreamingContent("");
          setCurrentStreamingMessageId(null);
          setIsMessageSending(false);
          streamContentRef.current = "";
        },
      );
    } catch (error) {
      console.error("Error in handleSend:", error);

      // Update the message with error and stop streaming
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === aiMessageId && !msg.isUser
            ? {
                ...msg,
                content: "Sorry, an error occurred. Please try again.",
                isStreaming: false,
              }
            : msg,
        ),
      );
      setCurrentStreamingMessageId(null);
      setIsMessageSending(false);
      setStreamingContent("");
      streamContentRef.current = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isMessageSending) return;
    await handleSend(input.trim());
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);

    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = "auto";
    const newHeight = Math.min(textarea.scrollHeight, 120); // Max 5 rows
    textarea.style.height = `${newHeight}px`;
  };

  // Auto-resize textarea when input changes (including programmatic changes)
  useEffect(() => {
    if (inputRef.current) {
      const textarea = inputRef.current;
      textarea.style.height = "auto";
      const newHeight = Math.min(textarea.scrollHeight, 120); // Max 5 rows
      textarea.style.height = `${newHeight}px`;
    }
  }, [input]);

  const handleCategorySelect = (category: { id: number; title: string }) => {
    setSelectedCategory(category);

    // Auto-scroll to center the selected category
    setTimeout(() => {
      if (categoriesScrollRef.current) {
        const container = categoriesScrollRef.current;
        const selectedButton = container.querySelector(
          `[data-category-id="${category.id}"]`,
        ) as HTMLElement;

        if (selectedButton) {
          const containerWidth = container.clientWidth;
          const buttonLeft = selectedButton.offsetLeft;
          const buttonWidth = selectedButton.offsetWidth;
          const scrollLeft = buttonLeft - containerWidth / 2 + buttonWidth / 2;

          container.scrollTo({
            left: Math.max(0, scrollLeft),
            behavior: "smooth",
          });
        }
      }
    }, 100);
  };

  const handleOpenCasesModal = () => {
    dispatch(
      showModal({
        type: ModalType.COMPONENT,
        title: "Select Clinical Case",
        width: "40%",
        component: (
          <CasesModal
            onSelectCase={(caseValue) => {
              setInput(caseValue);
              // Force textarea resize after setting the value
              setTimeout(() => {
                if (inputRef.current) {
                  const textarea = inputRef.current;
                  textarea.style.height = "auto";
                  const newHeight = Math.min(textarea.scrollHeight, 120);
                  textarea.style.height = `${newHeight}px`;
                }
              }, 0);
              dispatch({ type: "modal/hideModal" });
            }}
            onClose={() => dispatch({ type: "modal/hideModal" })}
          />
        ),
      }),
    );
  };

  // Show skeleton loader while categories are loading
  if (isLoadingCategories) {
    return <ClinicalCaseSkeleton />;
  }

  // Show error if no matching tab found
  if (!matchingTab) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center px-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Category Not Found
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              The requested category "{tabValue}" could not be found.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {messages.length === 0 ? (
            <div
              className={`h-full flex flex-col items-center ${isMobile ? "pt-[60px] sm:pt-[100px]" : "justify-center"} text-center px-4 overflow-y-auto`}
            >
              <h2 className="text-2xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary via-purple-500 to-primary/60 dark:from-primary-light dark:via-purple-400 dark:to-primary mb-4">
                Welcome, {currentUser?.first_name || "Doctor"}!
              </h2>
              {!isMobile && (
                <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 mb-8 sm:mb-12 w-full md:w-[60%] leading-relaxed">
                  {categories.length === 1
                    ? `I'm here to help you analyze clinical cases in ${categories[0].title}. Ask your clinical question below.`
                    : "I'm here to help you analyze clinical cases. Please select a category below and ask your clinical question."}
                </p>
              )}
              {categories.length > 1 && (
                <div className="w-full max-w-5xl relative">
                  {/* Only show scroll buttons if there are 6 or more categories */}
                  {categories.length >= 6 && !isMobile && (
                    <>
                      {/* Left scroll button - hidden on mobile */}
                      <button
                        onClick={() => scrollCategories("left")}
                        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-8 sm:-translate-x-12 z-10 w-8 h-8 bg-white dark:bg-[#1f1f1f] rounded-full border border-gray-200 dark:border-gray-700 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>

                      {/* Right scroll button - hidden on mobile */}
                      <button
                        onClick={() => scrollCategories("right")}
                        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-8 sm:translate-x-12 z-10 w-8 h-8 bg-white dark:bg-[#1f1f1f] rounded-full border border-gray-200 dark:border-gray-700 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
                      >
                        <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                    </>
                  )}

                  <div
                    ref={categoriesScrollRef}
                    className={`${categories.length >= 6 ? "overflow-x-auto overflow-y-hidden" : "overflow-visible"} ${!isMobile && categories.length >= 6 ? "px-8 sm:px-16" : "px-2"}`}
                    style={{
                      scrollbarWidth: categories.length >= 6 ? "none" : "auto", // Firefox
                      msOverflowStyle: categories.length >= 6 ? "none" : "auto", // IE/Edge
                    }}
                  >
                    <style>{`
                      .categories-container::-webkit-scrollbar {
                        display: ${categories.length >= 6 ? "none" : "auto"};
                      }
                    `}</style>
                    <div
                      className={`flex flex-col gap-2 sm:gap-3 py-2 h-full categories-container ${categories.length < 6 ? "items-center" : ""}`}
                      style={{
                        minWidth: isMobile
                          ? "auto"
                          : categories.length >= 6
                            ? "max-content"
                            : "auto",
                      }}
                    >
                      {/* Create rows based on category count */}
                      {(() => {
                        // For fewer categories, use a simpler layout
                        if (categories.length < 6) {
                          // Single row for 2-5 categories, centered
                          return (
                            <div className="flex gap-2 sm:gap-3 flex-wrap justify-center">
                              {categories.map(
                                (category: { id: number; title: string }) => (
                                  <button
                                    key={category.id}
                                    data-category-id={category.id}
                                    onClick={() =>
                                      handleCategorySelect(category)
                                    }
                                    disabled={isMessageSending}
                                    className={`group px-2 sm:px-3 py-2 rounded-lg bg-white dark:bg-[#1f1f1f] text-gray-800 dark:text-gray-300 hover:bg-primary/5 dark:hover:bg-primary/10 transition-all duration-200 border border-gray-200 dark:border-gray-800 hover:border-primary/30 dark:hover:border-primary/40 hover:text-primary text-center disabled:opacity-50 disabled:cursor-not-allowed relative flex-shrink-0 min-h-[36px] text-wrap ${
                                      selectedCategory?.id === category.id
                                        ? "border-primary/50 bg-primary/5 dark:bg-primary/10 text-primary"
                                        : ""
                                    }`}
                                  >
                                    <span className="text-xs font-medium pr-6 break-words whitespace-normal leading-relaxed">
                                      {category.title}
                                    </span>
                                    {selectedCategory?.id === category.id && (
                                      <div className="absolute top-1 right-1 w-4 h-4 bg-primary text-white rounded-full flex items-center justify-center">
                                        <Check className="w-2.5 h-2.5 flex-shrink-0" />
                                      </div>
                                    )}
                                  </button>
                                ),
                              )}
                            </div>
                          );
                        }

                        // Original logic for 6+ categories
                        // Sort categories by title length (longest first) for better distribution
                        const sortedCategories = [...categories].sort(
                          (a, b) => b.title.length - a.title.length,
                        );

                        // Initialize 3 rows for desktop, 1 row for mobile
                        const numRows = isMobile ? 1 : 3;
                        const rows: { id: number; title: string }[][] = Array(
                          numRows,
                        )
                          .fill([])
                          .map(() => []);
                        const rowWidths = Array(numRows).fill(0);

                        // Distribute items to balance row widths
                        sortedCategories.forEach(
                          (category: { id: number; title: string }) => {
                            // Find the row with the smallest current width
                            const minWidthIndex = rowWidths.indexOf(
                              Math.min(...rowWidths),
                            );
                            rows[minWidthIndex].push(category);
                            // Approximate width: roughly 8px per character + padding + gap
                            rowWidths[minWidthIndex] +=
                              category.title.length * 8 + 60;
                          },
                        );

                        return rows.map((rowItems, rowIndex) => (
                          <div
                            key={rowIndex}
                            className={`flex gap-2 sm:gap-3 flex-1 ${isMobile ? "flex-wrap justify-center" : ""}`}
                          >
                            {rowItems.map(
                              (category: { id: number; title: string }) => (
                                <button
                                  key={category.id}
                                  data-category-id={category.id}
                                  onClick={() => handleCategorySelect(category)}
                                  disabled={isMessageSending}
                                  className={`group px-2 sm:px-3 py-2 rounded-lg bg-white dark:bg-[#1f1f1f] text-gray-800 dark:text-gray-300 hover:bg-primary/5 dark:hover:bg-primary/10 transition-all duration-200 border border-gray-200 dark:border-gray-800 hover:border-primary/30 dark:hover:border-primary/40 hover:text-primary text-center disabled:opacity-50 disabled:cursor-not-allowed relative ${isMobile ? "flex-shrink-0 mb-2 max-w-[200px] min-w-full" : "flex-shrink-0"} min-h-[36px] text-wrap ${
                                    selectedCategory?.id === category.id
                                      ? "border-primary/50 bg-primary/5 dark:bg-primary/10 text-primary"
                                      : ""
                                  }`}
                                >
                                  <span className="text-xs font-medium pr-6 break-words whitespace-normal leading-relaxed">
                                    {category.title}
                                  </span>
                                  {selectedCategory?.id === category.id && (
                                    <div className="absolute top-1 right-1 w-4 h-4 bg-primary text-white rounded-full flex items-center justify-center">
                                      <Check className="w-2.5 h-2.5 flex-shrink-0" />
                                    </div>
                                  )}
                                </button>
                              ),
                            )}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {messages.map((message) => (
                <MessageItem
                  key={message.id}
                  message={message}
                  currentStreamingMessageId={currentStreamingMessageId}
                  streamingContent={streamingContent}
                />
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4">
          <form onSubmit={handleSubmit} className="relative max-w-4xl mx-auto">
            {/* Input container */}
            <div className="flex items-start bg-white dark:bg-[#1f1f1f] rounded-xl p-2">
              <div className="flex items-center pl-2 sm:pl-4 pr-2 sm:pr-3 pt-3">
                <Sparkles className="w-5 h-5 text-primary/60" />
              </div>

              <textarea
                ref={inputRef}
                value={input}
                onChange={handleTextareaChange}
                placeholder={
                  selectedCategory
                    ? "Ask your clinical question..."
                    : "Select a category first..."
                }
                disabled={!selectedCategory || isMessageSending}
                rows={1}
                className={`flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-gray-300 placeholder-gray-400 text-base py-3 pr-2 sm:pr-4 focus:ring-0 transition-all duration-300 resize-none ${
                  !selectedCategory || isMessageSending ? "opacity-50" : ""
                }`}
                style={{ minHeight: "44px", maxHeight: "120px" }}
              />

              {/* Desktop button - only show on desktop */}
              {!isMobile && deviceType !== DeviceTypes.tablet && (
                <div className="flex items-center gap-2 self-end">
                  {/* Flash button */}
                  <Button
                    variant={ButtonVariant.SECONDARY}
                    type="button"
                    onClick={handleOpenCasesModal}
                    disabled={!selectedCategory || isMessageSending}
                    className={`flex items-center justify-center bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400 rounded-xl px-3 py-3 transition-all duration-300 font-medium disabled:opacity-50 ${
                      !selectedCategory || isMessageSending ? "opacity-50" : ""
                    }`}
                    title="Select from predefined cases"
                  >
                    <Zap className="w-4 h-4" />
                  </Button>

                  {/* Send button */}
                  <Button
                    variant={ButtonVariant.PRIMARY}
                    type="submit"
                    disabled={
                      !input.trim() || !selectedCategory || isMessageSending
                    }
                    className={`flex items-center justify-center bg-primary hover:bg-primary/90 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-xl px-6 py-3 transition-all duration-300 font-medium disabled:opacity-50 ${
                      isMessageSending ? "animate-pulse" : ""
                    }`}
                  >
                    {isMessageSending ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <ArrowUp className="w-4 h-4 mr-2" />
                    )}
                    {isMessageSending ? "Sending..." : "Send"}
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile and Tablet buttons - below textarea */}
            {(isMobile || deviceType === DeviceTypes.tablet) && (
              <div className="flex gap-2 mt-3">
                {/* Flash button */}
                <Button
                  variant={ButtonVariant.SECONDARY}
                  type="button"
                  onClick={handleOpenCasesModal}
                  disabled={!selectedCategory || isMessageSending}
                  className={`flex items-center justify-center bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400 rounded-xl px-4 py-3 transition-all duration-300 font-medium disabled:opacity-50 ${
                    !selectedCategory || isMessageSending ? "opacity-50" : ""
                  }`}
                  title="Select from predefined cases"
                >
                  <Zap className="w-4 h-4" />
                </Button>

                {/* Send button */}
                <Button
                  variant={ButtonVariant.PRIMARY}
                  type="submit"
                  disabled={
                    !input.trim() || !selectedCategory || isMessageSending
                  }
                  className={`flex-1 flex items-center justify-center bg-primary hover:bg-primary/90 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-xl px-4 py-3 transition-all duration-300 font-medium disabled:opacity-50 ${
                    isMessageSending ? "animate-pulse" : ""
                  }`}
                >
                  {isMessageSending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <ArrowUp className="w-4 h-4 mr-2" />
                  )}
                  {isMessageSending ? "Sending..." : "Send"}
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClinicalCaseScreen;
